import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { useLanguage } from '../../shell/language-context';
import { DealRailCard, OfferRailCard, useRailViews } from './offer-rail-card';
import { useOfferPopup } from '../offers/offer-popup';
import { MarqueeControls, centreOffset } from './marquee-controls';
import type { HomeFormat } from './use-home-format';

type OfferCardData = components['schemas']['OfferCard'];
type DealData = components['schemas']['DirectoryOfferCard'];
/** One card in the rail: a bookable offer, or a deal a business announced itself (no booking; FR-A26 v3). */
type RailItem = { offer: OfferCardData; deal?: never } | { deal: DealData; offer?: never };

type ChipId = 'all' | 'food' | 'beauty' | 'stays' | 'ending' | 'bookable';
// The design's six chips. Three narrow by category (registry ids), two by offer
// facts the server already computed (ends_in_days, bookable).
const CHIPS: { id: ChipId; label: 'dc50.offers.chip_all' | 'dc50.offers.chip_food' | 'dc50.offers.chip_beauty' | 'dc50.offers.chip_stays' | 'dc50.offers.chip_ending' | 'dc50.offers.chip_bookable'; match: (o: RailItem) => boolean }[] = [
  { id: 'all', label: 'dc50.offers.chip_all', match: () => true },
  { id: 'food', label: 'dc50.offers.chip_food', match: (o) => categoryOf(o) === 'restaurant' },
  { id: 'beauty', label: 'dc50.offers.chip_beauty', match: (o) => categoryOf(o) === 'salon' },
  { id: 'stays', label: 'dc50.offers.chip_stays', match: (o) => categoryOf(o) === 'hotel' },
  { id: 'ending', label: 'dc50.offers.chip_ending', match: (o) => ((o.offer ?? o.deal).ends_in_days ?? 99) <= 2 },
  { id: 'bookable', label: 'dc50.offers.chip_bookable', match: (o) => Boolean(o.offer?.bookable) },
];
const categoryOf = (o: RailItem) => (o.offer ? (o.offer.category_id ?? o.offer.business.category_id) : o.deal.category_id);

// "Today's offers." (design DC:466-528, FR-C19; titled for the whole country until the rail
// follows the visitor's area — the home aggregate is not asked for one): context line, six filter chips, a
// looping marquee of offer cards with play/pause and a dot per offer, the empty line
// and the deal-alerts line. Chips filter the rail the home aggregate already carries;
// the Offers page (/offer) is where server-side filtering lives. Businesses' own announced
// deals follow the bookable offers.
export function OffersRailSection({ offers, deals, fmt }: { offers: OfferCardData[]; deals: DealData[]; fmt: HomeFormat }) {
  const { t } = useLanguage();
  const [chip, setChip] = useState<ChipId>('all');
  const [dot, setDot] = useState<number | null>(null);
  const [shift, setShift] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  const ctx = useRailViews(fmt);
  const { open, popup } = useOfferPopup();

  const items: RailItem[] = [...offers.map((offer) => ({ offer })), ...deals.map((deal) => ({ deal }))];
  const list = items.filter(CHIPS.find((c) => c.id === chip)!.match);
  // Enough copies that the loop never runs dry, then doubled for the -50% marquee.
  const half = list.length ? Array.from({ length: Math.max(1, Math.ceil(4 / list.length)) }, () => list).flat() : [];
  const loop = [...half, ...half];

  const select = (i: number) => {
    const rail = railRef.current;
    const first = rail?.querySelector<HTMLElement>('.ofp');
    setDot(i);
    setShift(centreOffset(i, rail?.clientWidth ?? window.innerWidth, first?.offsetWidth ?? 236));
  };

  return (
    <section className="hm-offers" data-reveal="out">
      <div className="hm-wrap">
        <div className="hm-head">
          <div>
            <h2 className="hm-h2">{t('dc50.offers.title')}</h2>
            <div className="hm-sub">{t('dc50.offers.context')}</div>
          </div>
          <Link className="hm-link" to="/offer">
            {t('dc50.offers.see_all')}
          </Link>
        </div>
        <div className="hm-offers__chips" role="toolbar" aria-label={t('dc50.offers.filter_label')}>
          {CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={chip === c.id}
              className={chip === c.id ? 'hm-chip is-on' : 'hm-chip'}
              onClick={() => {
                setChip(c.id);
                setDot(null);
              }}
            >
              {t(c.label)}
            </button>
          ))}
        </div>
        {list.length > 0 ? (
          <>
            <div className="hm-rail" ref={railRef}>
              <div
                className="hm-rail__track hm-rail__track--offers"
                data-motion="true"
                data-marquee="true"
                style={dot !== null ? { animation: 'none', transform: `translateX(${shift}px)` } : undefined}
              >
                {loop.map((o, i) =>
                  o.offer ? (
                    <OfferRailCard key={`${o.offer.id}-${i}`} offer={o.offer} ctx={ctx} onOpen={open} />
                  ) : (
                    <DealRailCard key={`${o.deal.id}-${i}`} deal={o.deal} ctx={ctx} onOpen={open} />
                  ),
                )}
              </div>
            </div>
            <MarqueeControls
              className="hm-mq--offers"
              count={list.length}
              selected={dot}
              onSelect={select}
              onToggle={() => (dot !== null ? setDot(null) : select(0))}
              playLabel={t('dc50.offers.autoplay')}
              dotLabel={(i) => t('dc50.offers.go_to', { n: fmt.num(i + 1) })}
            />
          </>
        ) : (
          <div className="hm-offers__empty">{t('dc50.offers.empty')}</div>
        )}
        <div className="hm-offers__alerts">{t('dc50.offers.alerts')}</div>
      </div>
      {popup}
    </section>
  );
}
